import React, { useState } from "react";
import { MediaImagePlus } from "iconoir-react";
import { useNotificationToast } from "@/components/ui/NotificationToast";

interface GroupData {
  id: string;
  name: string;
  description: string;
  rules: string;
  location: string;
  avatar?: string;
  coverImage?: string;
  memberCount: number;
}

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateGroup: (groupData: GroupData) => void;
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onCreateGroup,
}) => {
  const [groupData, setGroupData] = useState({
    name: "",
    description: "",
    rules: "",
    location: "",
  });

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [, setAvatarFile] = useState<File | null>(null);
  const [, setCoverFile] = useState<File | null>(null);
  const { showToast } = useNotificationToast();

  const handleInputChange = (field: string, value: string) => {
    setGroupData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setAvatarFile(file);
      setAvatarPreview(imageUrl);
    }
  };

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setCoverFile(file);
      setCoverPreview(imageUrl);
    }
  };

  const handleSubmit = () => {
    if (!groupData.name.trim()) return;

    const newGroup: GroupData = {
      id: Date.now().toString(),
      name: groupData.name,
      description: groupData.description,
      rules: groupData.rules,
      location: groupData.location,
      avatar: avatarPreview || undefined,
      coverImage: coverPreview || undefined,
      memberCount: 0,
    };

    onCreateGroup(newGroup);
    onClose();

    // Show success toast
    showToast("Tạo mới hội nhóm thành công", "success");

    // Reset form
    setGroupData({
      name: "",
      description: "",
      rules: "",
      location: "",
    });
    setAvatarPreview(null);
    setCoverPreview(null);
    setAvatarFile(null);
    setCoverFile(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop with blur effect */}
      <div
        className="absolute inset-0 backdrop-blur-sm bg-white/30"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-80 px-4 py-6 bg-[#FBF6F1] rounded-md shadow-[0px_1px_4px_0px_rgba(12,12,13,0.05)] flex flex-col justify-center items-start gap-6 max-h-[90vh] overflow-y-auto modal-scroll-hidden">
        <div className="self-stretch flex flex-col justify-start items-start gap-4">
          <div className="self-stretch text-center justify-start text-[#1E3A3A] text-xl font-semibold font-manrope leading-normal">
            Tạo hội nhóm
          </div>

          <div className="self-stretch inline-flex justify-start items-center gap-2.5 overflow-hidden">
            <div className="w-full p-3.5 rounded-md inline-flex flex-col justify-center items-start gap-4">
              <div className="self-stretch flex flex-col justify-start items-start gap-4">
                {/* Avatar Upload */}
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    id="avatar-upload"
                  />

                  {avatarPreview ? (
                    <div className="relative w-full">
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="w-full h-20 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarPreview(null);
                          setAvatarFile(null);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="avatar-upload"
                      className="self-stretch px-4 py-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] inline-flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    >
                      <div className="size- flex justify-start items-center gap-2.5">
                        <div className="size-5 relative overflow-hidden">
                          <MediaImagePlus className="w-5 h-5 text-[#1E3A3A]" />
                        </div>
                        <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                          Tải ảnh đại diện lên
                        </div>
                      </div>
                    </label>
                  )}
                </div>

                {/* Cover Upload */}
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    className="hidden"
                    id="cover-upload"
                  />

                  {coverPreview ? (
                    <div className="relative w-full">
                      <img
                        src={coverPreview}
                        alt="Cover preview"
                        className="w-full h-24 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCoverPreview(null);
                          setCoverFile(null);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="cover-upload"
                      className="self-stretch px-4 py-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] inline-flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    >
                      <div className="size- flex justify-start items-center gap-2.5">
                        <div className="size-5 relative overflow-hidden">
                          <MediaImagePlus className="w-5 h-5 text-[#1E3A3A]" />
                        </div>
                        <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                          Tải ảnh bìa lên
                        </div>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Group Name */}
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="size- inline-flex justify-start items-end gap-1">
                  <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                    Tên nhóm
                  </div>
                  <div className="text-center justify-start text-[#E06448] text-[10px] font-normal font-sora leading-none">
                    *Bắt buộc
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-between items-center">
                  <input
                    type="text"
                    value={groupData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Nhập..."
                    className="flex-1 p-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] text-[#6F6F6B] text-xs font-normal font-sora leading-none bg-white"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="size- inline-flex justify-start items-end gap-1">
                  <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                    Mô tả
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-between items-center">
                  <input
                    type="text"
                    value={groupData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Nhập..."
                    className="flex-1 p-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] text-[#6F6F6B] text-xs font-normal font-sora leading-none bg-white"
                  />
                </div>
              </div>

              {/* Rules */}
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="size- inline-flex justify-start items-end gap-1">
                  <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                    Quy tắc nhóm
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-between items-center">
                  <input
                    type="text"
                    value={groupData.rules}
                    onChange={(e) => handleInputChange("rules", e.target.value)}
                    placeholder="Nhập..."
                    className="flex-1 p-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] text-[#6F6F6B] text-xs font-normal font-sora leading-none bg-white"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="size- inline-flex justify-start items-end gap-1">
                  <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                    Vị trí
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-between items-center">
                  <input
                    type="text"
                    value={groupData.location}
                    onChange={(e) =>
                      handleInputChange("location", e.target.value)
                    }
                    placeholder="Nhập..."
                    className="flex-1 p-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] text-[#6F6F6B] text-xs font-normal font-sora leading-none bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="self-stretch inline-flex justify-start items-start gap-2">
          <div
            className="flex-1 px-4 py-3.5 bg-neutral-200 rounded-full flex justify-center items-center gap-2.5 cursor-pointer"
            onClick={onClose}
          >
            <div className="text-center justify-start text-[#1E3A3A] text-sm font-medium font-sora leading-none">
              Hủy
            </div>
          </div>
          <div
            className="flex-1 px-4 py-3.5 bg-[#E06448] rounded-full flex justify-center items-center gap-2.5 cursor-pointer"
            onClick={handleSubmit}
          >
            <div className="text-center justify-start text-[#F4E7DA] text-sm font-medium font-sora leading-none">
              Tạo nhóm
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;
